/**
 * ShopScreen — единый магазин Рублокса.
 *
 * Раньше алмазы тратились в ЧЕТЫРЁХ местах с четырьмя разными
 * интерфейсами: темы в отдельной вкладке, рамки и цвет ника — в модалке
 * внутри профиля, скины персонажа — внутри экрана «Аватар», обложка
 * профиля — ещё в одной модалке. Человек не мог ответить на простой
 * вопрос «на что я могу потратить алмазы».
 *
 * Покупка везде списывает из ЕДИНОГО кошелька v2 — того же, что в школе.
 *
 * При переводе на дизайн-систему исправлено три вещи:
 *  • «Примерить» НИЧЕГО не примеряла — только показывала плашку-подсказку.
 *    Теперь примерка реально красит кабинет (tryOnTheme в CabinetContext,
 *    живёт в памяти и не пишется в localStorage) и сбрасывается при уходе
 *    со страницы: унести неоплаченную тему в остальной кабинет нельзя;
 *  • кнопки при нажатии показывали «…» и меняли ширину — курсор
 *    промахивался. Теперь состояние loading с фиксированной шириной;
 *  • «Не хватает алмазов» показывалось тостом ПОСЛЕ клика. Теперь кнопка
 *    сразу выключена, а цена подсвечена — цель видна до нажатия.
 */
import React, { useCallback, useEffect, useState } from "react";
import { CAB_THEMES } from "../../../theme/cabThemes";
import { useCabinet } from "../../../context/CabinetContext";
import { useAuth } from "../../../context/AuthContext";
import * as cabinetApi from "../../../api/cabinet";
import CabIcon from "../CabIcon";
import ThemePreview from "../ThemePreview";
import AvatarFrame from "../AvatarFrame";
import { Button, Card, Pill, Tabs, Skeleton, EmptyState, fmtNum } from "../../ui";
import "../../../styles/cabinet-shop.css";

const TABS = [
  { id: "themes", label: "Темы" },
  { id: "frames", label: "Рамки" },
  { id: "colors", label: "Цвет ника" },
];

/** Редкость по цене — цвет подсказывает ценность до чтения цифры. */
function rarityOf(price) {
  if (!price) return { key: "base", title: "Базовая", tone: "neutral" };
  if (price <= 150) return { key: "common", title: "Обычная", tone: "info" };
  if (price <= 350) return { key: "rare", title: "Редкая", tone: "soft" };
  if (price <= 550) return { key: "epic", title: "Эпическая", tone: "warning" };
  return { key: "legendary", title: "Легендарная", tone: "accent" };
}

export default function ShopScreen() {
  const {
    appTheme, setAppTheme, previewTheme, tryOnTheme,
    diamonds, setDiamondsLocal,
  } = useCabinet();
  const { user } = useAuth();

  const [tab, setTab] = useState("themes");
  const [themes, setThemes] = useState([]);
  const [frames, setFrames] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState({});
  const [authLost, setAuthLost] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const flash = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 2800);
  }, []);

  /* Грузим все три каталога сразу: переключение вкладок должно быть
     мгновенным, а не с новой загрузкой на каждый клик. */
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    /* Раньше стояло `.catch(() => [])` на каждом запросе: ЛЮБАЯ ошибка
       (в том числе 401 при протухшей сессии) превращалась в пустой каталог,
       и человек видел «магазин, в котором ничего нет» вместо сообщения
       о проблеме. Отличить пустоту от сбоя было невозможно.
       Теперь провал каждой вкладки фиксируется отдельно: одна упавшая
       не должна прятать две работающие. */
    Promise.allSettled([
      cabinetApi.getAppThemes(ctrl.signal),
      cabinetApi.getFrames(ctrl.signal),
      cabinetApi.getNameColors(ctrl.signal),
    ])
      .then(([t, f, c]) => {
        if (cancelled) return;
        const take = (r) => (r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []);
        setThemes(take(t));
        setFrames(take(f));
        setColors(take(c));
        setFailed({
          themes: t.status === "rejected",
          frames: f.status === "rejected",
          colors: c.status === "rejected",
        });
        // 401 на всех трёх — это протухшая сессия, а не пустой каталог.
        const allAuth = [t, f, c].every(
          (r) => r.status === "rejected" && r.reason?.status === 401
        );
        setAuthLost(allAuth);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [reloadTick]);

  /* Уход со страницы снимает примерку. Без этого неоплаченная тема
     осталась бы на всём кабинете до перезагрузки. */
  useEffect(() => () => tryOnTheme(null), [tryOnTheme]);

  const spend = (price, res) => {
    if (typeof res?.diamonds === "number") setDiamondsLocal(res.diamonds);
    else if (typeof diamonds === "number") setDiamondsLocal(diamonds - price);
  };

  const notEnough = (price, currency) =>
    currency !== "star" && typeof diamonds === "number" && diamonds < price;

  /* ── темы ─────────────────────────────────────────────────────── */

  const buyTheme = async (key, price) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      const r = await cabinetApi.buyAppTheme(key);
      if (r?.ok === false) return flash(r.error || "Не удалось купить", true);
      setThemes((p) => p.map((t) => (t.key === key ? { ...t, owned: true } : t)));
      spend(price, r);
      flash("Тема куплена");
    } catch (e) {
      flash(e?.message || "Не удалось купить", true);
    } finally {
      setBusyKey(null);
    }
  };

  const applyTheme = async (key) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      await cabinetApi.activateAppTheme(key);
      setAppTheme(key);          // сам сбрасывает примерку
      flash("Тема применена");
    } catch (e) {
      flash(e?.message || "Не удалось применить", true);
    } finally {
      setBusyKey(null);
    }
  };

  /* ── рамки и цвета ────────────────────────────────────────────── */

  const buyItem = async (kind, key, price) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      const r =
        kind === "frame"
          ? await cabinetApi.buyFrame(key)
          : await cabinetApi.buyNameColor(key);
      if (r?.ok === false) return flash(r.error || "Не удалось купить", true);
      const setter = kind === "frame" ? setFrames : setColors;
      setter((p) => p.map((i) => (i.key === key ? { ...i, owned: true } : i)));
      spend(price, r);
      flash("Куплено");
    } catch (e) {
      flash(e?.message || "Не удалось купить", true);
    } finally {
      setBusyKey(null);
    }
  };

  const applyItem = async (kind, key) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      if (kind === "frame") await cabinetApi.activateFrame(key);
      else await cabinetApi.activateNameColor(key);
      const setter = kind === "frame" ? setFrames : setColors;
      setter((p) => p.map((i) => ({ ...i, active: i.key === key })));
      flash("Применено");
    } catch (e) {
      flash(e?.message || "Не удалось применить", true);
    } finally {
      setBusyKey(null);
    }
  };

  /* Купленное и активное — в начало: человек ищет своё, а не витрину. */
  const sortOwned = (list, activeKey) =>
    [...list].sort((a, b) => {
      const rank = (x) =>
        (activeKey ? x.key === activeKey : x.active) ? 0 : x.owned ? 1 : 2;
      return rank(a) - rank(b) || (a.price || 0) - (b.price || 0);
    });

  /* Цена + кнопка покупки. Общий блок для тем, рамок и цветов: раньше
     эта разметка была скопирована трижды и успела разъехаться. */
  const PriceBuy = ({ price, currency, busy, onBuy }) => {
    const poor = notEnough(price, currency);
    return (
      <>
        <span className={`rb-shop__price ${poor ? "is-poor" : ""}`}>
          <CabIcon name={currency === "star" ? "star" : "diamond"} size={15} />
          {fmtNum(price)}
        </span>
        <Button
          variant="primary"
          size="sm"
          onClick={onBuy}
          loading={busy}
          disabled={poor}
          title={poor ? "Не хватает алмазов" : undefined}
        >
          Купить
        </Button>
      </>
    );
  };

  /* Список активной вкладки — чтобы отличить «пусто» от «не загрузилось». */
  const currentList = tab === "themes" ? themes : tab === "frames" ? frames : colors;

  const skeletonGrid = (small) => (
    <div className={`rb-shop__grid ${small ? "rb-shop__grid--small" : ""}`}>
      {Array.from({ length: small ? 8 : 4 }).map((_, i) => (
        <Card key={i} pad0>
          <Skeleton h={small ? 96 : 132} r={0} />
          <div className="rb-shop__body">
            <Skeleton h={15} w="62%" />
            <Skeleton h={28} w="100%" style={{ marginTop: 12 }} />
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="ui-page">
      <div className="rb-shop__head">
        <div className="rb-shop__head-text">
          <h1 className="rb-shop__title">Магазин</h1>
          <p className="rb-shop__sub">
            Оформление кабинета и профиля. Кошелёк общий со школой —
            купленное работает в обоих проектах.
          </p>
        </div>
        <div className="rb-shop__balance">
          <CabIcon name="diamond" size={19} />
          <span className="ui-num">{fmtNum(diamonds)}</span>
        </div>
      </div>

      <Tabs items={TABS} value={tab} onChange={setTab} className="rb-shop__tabs" />

      {/* Примерка активна — держим подсказку на виду. Она объясняет, почему
          кабинет вдруг сменил цвет, и даёт выход одним кликом. */}
      {previewTheme && (
        <Card accent className="rb-shop__tryon">
          <CabIcon name="eye" size={19} />
          <span>
            Примеряешь «{CAB_THEMES[previewTheme]?.title}». Это только просмотр —
            чтобы оставить, нажми «Применить».
          </span>
          <Button variant="ghost" size="sm" onClick={() => tryOnTheme(null)}>
            Снять
          </Button>
        </Card>
      )}

      {/* Сессия протухла — это не «магазин пуст», а «мы не смогли спросить».
          Раньше и то и другое выглядело одинаково: белый экран. */}
      {!loading && authLost ? (
        <EmptyState
          icon={<CabIcon name="lock" size={40} />}
          title="Нужно войти заново"
          desc="Сессия истекла, поэтому магазин не загрузился."
          action={
            <Button variant="primary" onClick={() => window.location.assign("/login")}>
              Войти
            </Button>
          }
        />
      ) : !loading && failed[tab] ? (
        <EmptyState
          icon={<CabIcon name="warning" size={40} />}
          title="Не получилось загрузить"
          desc="Проверь соединение и попробуй ещё раз."
          action={
            <Button variant="primary" onClick={() => { setLoading(true); setReloadTick((t) => t + 1); }}>
              Повторить
            </Button>
          }
        />
      ) : !loading && currentList.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="cart" size={40} />}
          title="Здесь пока пусто"
          desc="Товары этого раздела скоро появятся."
        />
      ) : loading ? (
        skeletonGrid(tab !== "themes")
      ) : tab === "themes" ? (
        <div className="rb-shop__grid">
          {sortOwned(themes, appTheme).map((t) => {
            const meta = CAB_THEMES[t.key];
            if (!meta) return null;
            const rarity = rarityOf(t.price);
            const isActive = t.key === appTheme;
            const isPreview = t.key === previewTheme;
            const busy = busyKey === t.key;

            return (
              <Card key={t.key} pad0 className="rb-shop__card">
                <div className="rb-shop__preview">
                  <ThemePreview themeKey={t.key} />
                  {!t.owned && (
                    <span className="rb-shop__lock" title="Ещё не куплена">
                      <CabIcon name="lock" size={16} />
                    </span>
                  )}
                </div>
                <div className="rb-shop__body">
                  <div className="rb-shop__title-row">
                    <span className="rb-shop__name">{meta.title}</span>
                    <Pill tone={rarity.tone}>{rarity.title}</Pill>
                  </div>
                  <div className="rb-shop__foot">
                    {isActive ? (
                      <Pill tone="success" icon={<CabIcon name="check" size={13} />}>
                        Применена
                      </Pill>
                    ) : t.owned ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => applyTheme(t.key)}
                        loading={busy}
                      >
                        Применить
                      </Button>
                    ) : (
                      <PriceBuy
                        price={t.price}
                        busy={busy}
                        onBuy={() => buyTheme(t.key, t.price)}
                      />
                    )}
                    {!isActive && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => tryOnTheme(isPreview ? null : t.key)}
                      >
                        {isPreview ? "Снять" : "Примерить"}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : tab === "frames" ? (
        <div className="rb-shop__grid rb-shop__grid--small">
          {sortOwned(frames).map((f) => {
            const busy = busyKey === f.key;
            return (
              <Card key={f.key} className="rb-shop__item">
                {/* Рамку показываем на ЖИВОМ аватаре: по названию «Радуга»
                    или «Орбита» невозможно понять, как она выглядит. */}
                <div className="rb-shop__item-preview">
                  <AvatarFrame frame={f.key} size={72} thickness={4}>
                    {user?.photo ? (
                      <img src={user.photo} alt="" className="rb-shop__item-ava" />
                    ) : (
                      <span className="rb-shop__item-ava rb-shop__item-ava--letter">
                        {(user?.username || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </AvatarFrame>
                </div>
                <span className="rb-shop__name">{f.title || f.key}</span>
                <div className="rb-shop__foot">
                  {f.active ? (
                    <Pill tone="success" icon={<CabIcon name="check" size={13} />}>
                      Надета
                    </Pill>
                  ) : f.owned ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => applyItem("frame", f.key)}
                      loading={busy}
                    >
                      Надеть
                    </Button>
                  ) : (
                    <PriceBuy
                      price={f.price}
                      currency={f.currency}
                      busy={busy}
                      onBuy={() => buyItem("frame", f.key, f.price)}
                    />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="rb-shop__grid rb-shop__grid--small">
          {sortOwned(colors).map((c) => {
            const busy = busyKey === c.key;
            const isRainbow = c.key === "rainbow" || c.color === "rainbow";
            return (
              <Card key={c.key} className="rb-shop__item">
                {/* Имя показываем ТЕМ САМЫМ цветом — иначе выбирать
                    приходится вслепую по названию. */}
                <span
                  className={`rb-shop__color-demo ${isRainbow ? "is-rainbow" : ""}`}
                  style={isRainbow ? undefined : { color: c.color }}
                >
                  {user?.username || "Ник"}
                </span>
                <span className="rb-shop__name">{c.title || c.key}</span>
                <div className="rb-shop__foot">
                  {c.active ? (
                    <Pill tone="success" icon={<CabIcon name="check" size={13} />}>
                      Активен
                    </Pill>
                  ) : c.owned ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => applyItem("color", c.key)}
                      loading={busy}
                    >
                      Применить
                    </Button>
                  ) : (
                    <PriceBuy
                      price={c.price}
                      currency={c.currency}
                      busy={busy}
                      onBuy={() => buyItem("color", c.key, c.price)}
                    />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {toast && (
        <div
          className={`rb-shop__toast ${toast.isError ? "is-error" : ""}`}
          role="status"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
